import { useQuery, type ApolloError } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isEmpty } from '@newfront-insurance/core';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { useChatOverlayContext } from './chat-overlay-context';
import type { NovaSkill } from '../../../queries/chats';
import { GET_SKILLS } from '../../../queries/chats';

interface SkillLookup {
  skillId?: string | null;
  name?: string | null;
}

interface SkillsContextValue {
  skills: NovaSkill[];
  isLoading: boolean;
  error?: ApolloError;
  findSkill: (lookup: SkillLookup) => NovaSkill | undefined;
  getSkillDescription: (lookup: SkillLookup) => string | undefined;
}

const SkillsContext = createContext<SkillsContextValue | undefined>(undefined);

function normalizeSkillName(name?: string | null) {
  return name?.trim().toLowerCase() ?? '';
}

export function SkillsProvider({ children }: { children: ReactNode }) {
  const { userUuid } = useChatOverlayContext();
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);
  const { data, loading, error } = useQuery(GET_SKILLS, {
    skip: !isNovaSkillsEnabled || isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const skills = useMemo(() => (isNovaSkillsEnabled ? (data?.skills ?? []) : []), [isNovaSkillsEnabled, data?.skills]);
  const skillsById = useMemo(() => new Map(skills.map((skill) => [skill.id, skill])), [skills]);
  const skillsByName = useMemo(() => new Map(skills.map((skill) => [normalizeSkillName(skill.name), skill])), [skills]);

  const findSkill = useCallback(
    ({ skillId, name }: SkillLookup) => {
      if (skillId) {
        const skill = skillsById.get(skillId);
        if (skill) {
          return skill;
        }
      }

      const normalizedName = normalizeSkillName(name);
      return normalizedName ? skillsByName.get(normalizedName) : undefined;
    },
    [skillsById, skillsByName],
  );

  const getSkillDescription = useCallback(
    (lookup: SkillLookup) => findSkill(lookup)?.description?.trim() || undefined,
    [findSkill],
  );

  const value = useMemo<SkillsContextValue>(
    () => ({
      skills,
      isLoading: isNovaSkillsEnabled ? loading : false,
      error: isNovaSkillsEnabled ? (error ?? undefined) : undefined,
      findSkill,
      getSkillDescription,
    }),
    [skills, isNovaSkillsEnabled, loading, error, findSkill, getSkillDescription],
  );

  return <SkillsContext.Provider value={value}>{children}</SkillsContext.Provider>;
}

export function useSkills(): SkillsContextValue {
  const context = useContext(SkillsContext);
  if (!context) {
    throw new Error('useSkills must be used within SkillsProvider');
  }
  return context;
}

export function useSkillsSafe() {
  return useContext(SkillsContext);
}
